import React, { useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
    MdKeyboardArrowDown,
    MdOutlineAddTask,
    MdOutlineCheckCircle,
    MdOutlineInfo,
    MdOutlinePersonAddAlt,
    MdOutlineRocketLaunch,
} from "react-icons/md"
import { useAuth } from "../../../contexts/authContext"
import ProgressSetup from "./ProgressSetup"
import styles from "./onboarding.module.css"

const QuickTutorial = ({ step = 0, totalSteps = 1, standalone = false, funnelId, initialProgress, onProgressChange = async () => { }, nextStep = () => { } }) => {
    const { account_id } = useParams()
    const { permissions, verifyIfModuleIsActive } = useAuth()
    const [completed, setCompleted] = useState(initialProgress?.completedTasks || [])
    const [expanded, setExpanded] = useState(initialProgress?.currentTask || "lead")

    const tasks = useMemo(() => {
        const kanbanPath = `/${account_id}/kanban/${funnelId || ""}`
        const availableTasks = [
            {
                id: "lead",
                title: "Cadastre seu primeiro lead",
                summary: "Crie o contato que será acompanhado no seu funil.",
                prerequisite: "Seu funil precisa estar criado — isso já foi feito nas etapas anteriores.",
                steps: [
                    "Abra o Kanban e localize a primeira etapa do funil.",
                    "Clique no botão + localizado no cabeçalho dessa etapa.",
                    "Informe o nome e pelo menos um contato, como telefone ou e-mail.",
                    "Revise a etapa e o responsável e clique em Salvar.",
                ],
                tip: "Comece com um contato de teste da sua própria equipe para praticar sem risco.",
                path: kanbanPath,
                link: "Abrir Kanban para cadastrar",
                icon: <MdOutlinePersonAddAlt />,
                visible: permissions?.crm >= 1,
            },
            {
                id: "task",
                title: "Agende um acompanhamento",
                summary: "Crie uma tarefa dentro do lead, onde ela fica ligada ao contexto correto.",
                prerequisite: "Cadastre ao menos um lead antes. A lista geral de tarefas serve para acompanhar tarefas já criadas.",
                steps: [
                    "No Kanban, clique no card do lead que você cadastrou.",
                    "No painel do lead, abra a aba Tarefas.",
                    "Clique no botão + ao lado do título Tarefas.",
                    "Escolha o tipo, responsável, data e horário e salve a tarefa.",
                    "Depois, consulte todas as pendências no menu Lista de Tarefas.",
                ],
                tip: "Use tarefas para registrar a próxima ação concreta, como ligar, enviar proposta ou retornar o contato.",
                path: kanbanPath,
                link: "Abrir Kanban e escolher um lead",
                icon: <MdOutlineAddTask />,
                visible: permissions?.tasks >= 1 && permissions?.crm >= 1,
            },
            {
                id: "integration",
                title: "Conecte uma fonte de leads",
                summary: "Faça novos contatos chegarem ao funil sem cadastro manual.",
                prerequisite: "Tenha acesso de configuração às integrações da conta.",
                steps: [
                    "Abra Integrações e escolha a origem que sua empresa utiliza.",
                    "Em Formulários ou Meta Ads, selecione o funil e a etapa de destino.",
                    "Siga a autenticação solicitada e salve a configuração.",
                    "Envie um contato de teste e confirme se ele apareceu no Kanban.",
                ],
                tip: "Faça sempre um teste completo antes de publicar uma campanha ou formulário.",
                path: `/${account_id}/integrations`,
                link: "Escolher uma integração",
                icon: <MdOutlineRocketLaunch />,
                visible: permissions?.integrations >= 1,
            },
            {
                id: "chat",
                title: "Atenda sua primeira conversa",
                summary: "Centralize o atendimento depois que uma caixa de entrada estiver conectada.",
                prerequisite: "Conecte primeiro um canal de atendimento, como WhatsApp API ou WhatsApp Lite.",
                steps: [
                    "Abra o Chat e selecione uma conversa na coluna esquerda.",
                    "Leia o histórico e confirme se o atendimento está atribuído corretamente.",
                    "Digite a mensagem no campo inferior e envie a resposta.",
                    "Ao finalizar, atualize o status da conversa conforme o processo da equipe.",
                ],
                tip: "Se nenhuma conversa aparecer, envie uma mensagem de teste para o número conectado.",
                path: `/${account_id}/chat`,
                link: "Abrir Chat",
                icon: <MdOutlineCheckCircle />,
                visible: verifyIfModuleIsActive("CHAT") && permissions?.chat >= 1,
            },
        ]

        return availableTasks.filter(task => task.visible)
    }, [account_id, funnelId, permissions, verifyIfModuleIsActive])

    useEffect(() => {
        if (!tasks.length || expanded === null) return
        if (!tasks.some(task => task.id === expanded)) {
            setExpanded(tasks.find(task => !completed.includes(task.id))?.id || tasks[0].id)
        }
    }, [tasks, completed, expanded])

    const toggleTask = async (taskId) => {
        const previousCompleted = completed
        const previousExpanded = expanded
        const isCompleted = completed.includes(taskId)
        const nextCompleted = isCompleted
            ? completed.filter(id => id !== taskId)
            : [...completed, taskId]
        const currentIndex = tasks.findIndex(task => task.id === taskId)
        const nextTask = !isCompleted ? tasks[currentIndex + 1] : null
        const nextExpanded = !isCompleted ? (nextTask?.id || null) : taskId
        const persistedTask = nextTask?.id || taskId

        setCompleted(nextCompleted)
        setExpanded(nextExpanded)
        try {
            await onProgressChange({ completedTasks: nextCompleted, currentTask: persistedTask })
        } catch (err) {
            setCompleted(previousCompleted)
            setExpanded(previousExpanded)
        }
    }

    const toggleExpanded = async (taskId, isExpanded) => {
        const previousExpanded = expanded
        const nextExpanded = isExpanded ? null : taskId
        setExpanded(nextExpanded)
        if (nextExpanded) {
            try {
                await onProgressChange({ completedTasks: completed, currentTask: nextExpanded })
            } catch (err) {
                setExpanded(previousExpanded)
            }
        }
    }

    const reviewedTasks = completed.filter(taskId => tasks.some(task => task.id === taskId))

    return (
        <ProgressSetup
            step={step}
            totalSteps={totalSteps}
            showProgress={!standalone}
            stepLabel={standalone ? "Guia inicial" : undefined}
            title="Aprenda o essencial fazendo"
            description={`${reviewedTasks.length} de ${tasks.length} tutoriais revisados. Siga os passos na ordem: cada ação prepara a próxima.`}
            btnText="Concluir tutorial"
            btnAltText={standalone ? undefined : "Continuar depois"}
            onClick={nextStep}
            onClickAlt={nextStep}
        >
            <div className={styles.tutorialList}>
                {tasks.map((task, index) => {
                    const isCompleted = completed.includes(task.id)
                    const isExpanded = expanded === task.id
                    return (
                        <article className={`${styles.tutorial} ${isCompleted ? styles.tutorialCompleted : ""}`} key={task.id}>
                            <button className={styles.tutorialHeader} type="button" onClick={() => toggleExpanded(task.id, isExpanded)} aria-expanded={isExpanded}>
                                <span className={styles.tutorialIndex}>{isCompleted ? <MdOutlineCheckCircle /> : index + 1}</span>
                                <span className={styles.tutorialHeading}>
                                    <span>{task.icon}<strong>{task.title}</strong></span>
                                    <small>{task.summary}</small>
                                </span>
                                <MdKeyboardArrowDown className={`${styles.tutorialArrow} ${isExpanded ? styles.tutorialArrowOpen : ""}`} />
                            </button>
                            {isExpanded && <div className={styles.tutorialBody}>
                                <div className={styles.prerequisite}>
                                    <MdOutlineInfo />
                                    <div><strong>Antes de começar</strong><span>{task.prerequisite}</span></div>
                                </div>
                                <ol className={styles.instructions}>
                                    {task.steps.map((instruction, instructionIndex) => (
                                        <li key={instruction}>
                                            <span>{instructionIndex + 1}</span>
                                            <p>{instruction}</p>
                                        </li>
                                    ))}
                                </ol>
                                <div className={styles.tutorialTip}><strong>Dica:</strong> {task.tip}</div>
                                <div className={styles.tutorialActions}>
                                    <Link to={task.path} target="_blank" rel="noreferrer">{task.link}</Link>
                                    <button type="button" onClick={() => toggleTask(task.id)}>
                                        {isCompleted ? "Marcar como pendente" : "Marcar como revisado"}
                                    </button>
                                </div>
                            </div>}
                        </article>
                    )
                })}
            </div>
        </ProgressSetup>
    )
}

export default QuickTutorial
